import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { completeLogin, getStoredUser, renewSession, startLogin, startLogout, userManager } from '@/auth/oidc.js';

export type ServiceRole = 'ADMIN' | 'USER';

/**
 * Текущий пользователь intranet-web.
 *
 * Идентичность приходит из JWT. Права — локальные для сервиса (из нашей БД),
 * подтверждаются бэком через `GET /api/v1/me`: глобальная роль и список
 * модулей, которые пользователь модерирует. С Keycloak-ролями не связаны.
 */
export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  role: ServiceRole;
  moderatorOf: string[];
  employeeId?: string;
}

interface MeResponse {
  id: string;
  email?: string;
  displayName?: string;
  isServiceAccount: boolean;
  role: ServiceRole;
  moderatorOf: string[];
  employeeId?: string;
}

const apiBase = import.meta.env.VITE_API_URL.replace(/\/$/, '');

/**
 * Результат запроса /me:
 *   - ok      — профиль получен
 *   - denied  — 403: пользователь залогинен в Keycloak, но интранет ему
 *               недоступен (не тот домен / email не подтверждён). message —
 *               человекочитаемая причина от бэка.
 *   - error   — прочая ошибка (сеть/5xx); трактуем мягко, как раньше.
 */
type MeResult =
  | { status: 'ok'; me: MeResponse }
  | { status: 'denied'; message: string }
  | { status: 'error' };

async function fetchMe(token: string): Promise<MeResult> {
  try {
    const r = await fetch(`${apiBase}/api/v1/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (r.status === 403) {
      let message = '';
      try {
        const body = (await r.json()) as { message?: string };
        message = body?.message ?? '';
      } catch {
        // тело не json — оставим пустое сообщение, покажем дефолт
      }
      return { status: 'denied', message };
    }
    if (!r.ok) return { status: 'error' };
    return { status: 'ok', me: (await r.json()) as MeResponse };
  } catch {
    return { status: 'error' };
  }
}

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>(null);
  const user = ref<CurrentUser | null>(null);
  const isLoading = ref(true);
  /** Причина отказа во входе в интранет (403 от /me), null — доступ есть. */
  const accessDenied = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value && !!accessToken.value);
  const isAdmin = computed(() => user.value?.role === 'ADMIN');

  function isModeratorOf(moduleKey: string): boolean {
    const u = user.value;
    if (!u) return false;
    return u.role === 'ADMIN' || u.moderatorOf.includes(moduleKey);
  }

  async function hydrate(oidcUser: import('oidc-client-ts').User | null): Promise<void> {
    if (!oidcUser || oidcUser.expired) {
      accessToken.value = null;
      user.value = null;
      accessDenied.value = null;
      return;
    }
    accessToken.value = oidcUser.access_token;

    const result = await fetchMe(oidcUser.access_token);
    if (result.status === 'denied') {
      // Залогинен в Keycloak, но интранет недоступен. Токен оставляем, чтобы
      // работал выход; user не заполняем — в приложение не пускаем.
      accessDenied.value = result.message || 'Доступ к интранету ограничен.';
      user.value = null;
      return;
    }
    accessDenied.value = null;

    const me = result.status === 'ok' ? result.me : null;
    const profile = oidcUser.profile as Record<string, unknown>;
    const firstName = (profile.given_name as string | undefined) ?? '';
    const lastName = (profile.family_name as string | undefined) ?? '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ');
    const profileEmail = (profile.email as string | undefined) ?? '';

    user.value = {
      id: oidcUser.profile.sub,
      email: me?.email ?? profileEmail,
      displayName:
        me?.displayName ||
        fullName ||
        (profile.preferred_username as string | undefined) ||
        profileEmail,
      role: me?.role ?? 'USER',
      moderatorOf: me?.moderatorOf ?? [],
      employeeId: me?.employeeId,
    };
  }

  async function initialize(): Promise<void> {
    isLoading.value = true;
    try {
      // Приложение открыли после паузы: токен доступа уже истёк, но токен
      // продления жив — продлеваем тихо, без страницы входа.
      const stored = await getStoredUser();
      await hydrate(stored?.expired ? ((await renewSession()) ?? stored) : stored);

      userManager.events.addUserLoaded((u) => {
        void hydrate(u);
      });
      userManager.events.addUserUnloaded(() => {
        void hydrate(null);
      });
      userManager.events.addAccessTokenExpired(() => {
        // Сначала пробуем продлить; выходим, только если не получилось.
        void renewSession().then((u) => (u ? undefined : hydrate(null)));
      });
      // На iPhone свёрнутое приложение засыпает вместе с таймером продления —
      // при возврате проверяем срок сами и продлеваем заранее.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        void getStoredUser().then((u) => {
          if (u && (u.expires_in ?? 0) < 60) void renewSession();
        });
      });
    } finally {
      isLoading.value = false;
    }
  }

  async function login(returnTo?: string): Promise<void> {
    await startLogin(returnTo);
  }

  async function handleCallback(): Promise<string> {
    const { user: oidcUser, returnTo } = await completeLogin();
    await hydrate(oidcUser);
    return returnTo;
  }

  async function logout(): Promise<void> {
    // Пока токен жив: сервер перестаёт слать пуш на это устройство.
    // Динамический импорт — без цикла auth → push → api-клиент → auth.
    await import('@/composables/usePush.js').then((m) => m.forgetPushOnLogout()).catch(() => undefined);
    await startLogout();
  }

  return {
    accessToken,
    user,
    accessDenied,
    isAuthenticated,
    isAdmin,
    isModeratorOf,
    isLoading,
    initialize,
    login,
    handleCallback,
    logout,
  };
});
